import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Floating prompt in the bottom-right corner of every page: asks whether the
// user needs something added or changed and sends them to the request form.
// Rendered from NavListAuth so it follows the user around the app.
//
// The card opens itself once per browser session and, once dismissed, stays
// closed for DISMISS_DAYS — the small bulb button is always there for anyone
// who wants it sooner.
const DISMISS_KEY = "nodeFeatureRequestPromptDismissed";
const SESSION_KEY = "nodeFeatureRequestPromptShown";
const DISMISS_DAYS = 14;
const OPEN_DELAY_MS = 4000;

// Gap kept between the bubble and whatever is below it.
const GAP_PX = 16;

// Pages where the prompt would be redundant or in the way.
const HIDDEN_PATHS = [
  "/forms/feature-request",
  "/admin/feature-requests",
  "/inituser",
  "/logout",
];

// Storage throws in some privacy modes — a prompt that can't remember a
// dismissal is better than a page that crashes.
const readStore = (store, key) => {
  try {
    return window[store].getItem(key);
  } catch (err) {
    return null;
  }
};
const writeStore = (store, key, value) => {
  try {
    window[store].setItem(key, value);
  } catch (err) {
    /* ignore — the prompt just asks again next time */
  }
};

// The monthly feedback check-in (FeedbackCheckIn.js) asks the same question
// in a popup; on a session where it opens, this card doesn't open on its own.
export const holdBackFeatureRequestPrompt = () =>
  writeStore("sessionStorage", SESSION_KEY, "1");

const dismissedRecently = () => {
  const at = Number(readStore("localStorage", DISMISS_KEY));
  if (!at) return false;
  return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

// How far off the bottom of the viewport the bubble has to sit to stay clear
// of the footer, the impersonation banner, and sticky action bars. Each is
// only in the way once it's actually on screen, so this is the height of
// whichever one is currently intruding the most, not a fixed reservation.
const measureBottomOffset = () => {
  let intrusion = 0;

  const footer = document.querySelector("[data-app-footer]");
  if (footer) {
    const overlap = window.innerHeight - footer.getBoundingClientRect().top;
    if (overlap > intrusion) intrusion = overlap;
  }

  // Bottom bars: the impersonation banner (fixed to the bottom edge) and
  // pages' sticky action bars (e.g. the RFQ's Back/Continue). Each one only
  // counts while it's actually docked at the bottom of the viewport — a
  // sticky bar that has scrolled back into its normal place mid-page isn't
  // in the way.
  document.querySelectorAll("[data-bottom-bar]").forEach((bar) => {
    const rect = bar.getBoundingClientRect();
    if (!rect.height || rect.bottom < window.innerHeight - 48) return;
    const overlap = window.innerHeight - rect.top;
    if (overlap > intrusion) intrusion = overlap;
  });

  return Math.max(0, intrusion) + GAP_PX;
};

const FeatureRequestBubble = () => {
  const [open, setOpen] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(GAP_PX);
  const navigate = useNavigate();
  const location = useLocation();

  const hidden = HIDDEN_PATHS.some((path) =>
    location.pathname.toLowerCase().startsWith(path)
  );

  // Auto-open once per session, a few seconds in so it doesn't compete with
  // the page the user actually came for.
  useEffect(() => {
    if (hidden || dismissedRecently()) return;
    if (readStore("sessionStorage", SESSION_KEY)) return;

    const timer = setTimeout(() => {
      // Checked again: the feedback check-in may have claimed the session.
      if (readStore("sessionStorage", SESSION_KEY)) return;
      writeStore("sessionStorage", SESSION_KEY, "1");
      setOpen(true);
    }, OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hidden]);

  // Ride up the page instead of sitting on top of the footer. Re-measured on
  // scroll and resize (rAF-throttled), and on route changes since page height
  // and the footer's position change with the route.
  useEffect(() => {
    if (hidden) return;

    let frame = null;
    const measure = () => {
      frame = null;
      setBottomOffset(measureBottomOffset());
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };

    // The footer renders after this component, so measure on the next frame.
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [hidden, location.pathname, open]);

  if (hidden) return null;

  const dismiss = () => {
    writeStore("localStorage", DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const goToForm = () => {
    setOpen(false);
    // Pass the page they were on so the form can prefill it.
    navigate(
      `/forms/feature-request?page=${encodeURIComponent(location.pathname)}`
    );
  };

  return (
    <div
      className="fixed right-4 z-40 print:hidden"
      style={{ bottom: `${bottomOffset}px` }}
    >
      {open ? (
        <div className="app-glass app-glass-rim w-[19rem] max-w-[calc(100vw-2rem)] rounded-2xl border p-4">
          <div className="flex items-start gap-3">
            <span className="app-icon-tile h-9 w-9 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-300">
              <LightbulbOutlinedIcon fontSize="small" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold">
                Need something added or changed?
              </p>
              <p className="app-muted mt-1 text-xs">
                If a feature would make your job easier — or something here
                should work differently — tell us and we'll take a look.
              </p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss request prompt"
              className="app-muted -mr-1 -mt-1 shrink-0 rounded-full p-1 transition-colors hover:bg-[color:var(--app-hover)]"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={goToForm}
              className="app-btn app-btn-primary flex-1"
            >
              Submit a Request
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="app-btn app-btn-ghost app-muted"
            >
              Not now
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Request a feature or change"
          title="Request a feature or change"
          className="app-glass app-glass-rim flex h-11 w-11 items-center justify-center rounded-full border text-amber-600 transition-transform hover:scale-105 active:scale-95 dark:text-amber-300"
        >
          <LightbulbOutlinedIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

export default FeatureRequestBubble;
